import { arrayHasAtLeastOneItem } from "common/utils/arrays";
import {
  WorkflowCollaboratorFieldsFragment,
  WorkflowStepCollaboratorsQuery,
} from "../../generated/graphql";
import { FlexColumn } from "../Common/__styles__/Layout";
import { Loading } from "../Common/Icons/Loading";
import { CollaboratorListContainer } from "../Common/ShareForm/__styles__/CollaboratorList";
import { ReadOnlyCollaboratorRow } from "../Common/ShareForm/ReadOnlyCollaboratorRow";
import { ShareForm } from "../Common/ShareForm/ShareForm";
import {
  Collaborator,
  InheritedPermissionGroup,
  PermissionOption,
} from "../Common/ShareForm/types";
import { Body } from "../Common/Typography";

export const WORKFLOW_STEP_PERMISSION_OPTIONS: PermissionOption[] = [
  { value: "createResource", label: "Can create" },
  { value: "updateResource", label: "Can update" },
  { value: "viewResource", label: "Can view" },
];

export type StepCollaborator = NonNullable<
  WorkflowStepCollaboratorsQuery["workflowStep"]
>["collaborators"][number];

const getEntityName = (
  entity: WorkflowCollaboratorFieldsFragment["publicUserOrInvite"],
) => {
  if (entity.__typename === "PublicUser") {
    return `${entity.firstName} ${entity.lastName}`.trim() || entity.email;
  }
  return entity.email;
};

export const mapIndividualCollaboratorsFromStep = (
  collaborators: StepCollaborator[],
): Collaborator[] => {
  if (!collaborators) return [];

  const individualCollaborators = collaborators.filter(
    (
      collab,
    ): collab is Extract<
      StepCollaborator,
      { __typename?: "WorkflowCollaborator" }
    > => collab.__typename === "WorkflowCollaborator",
  );

  const permissionRank: Record<string, number> = {
    createResource: 3,
    updateResource: 2,
    viewResource: 1,
  };

  const collaboratorMap = new Map<string, Collaborator>();

  individualCollaborators.forEach(collab => {
    const entity = collab.publicUserOrInvite;
    const name = getEntityName(entity);
    const description = entity.email;

    const existing = collaboratorMap.get(collab.entityId);
    const currentRank = permissionRank[collab.policy] || 0;
    const existingRank = existing
      ? permissionRank[existing.permission] || 0
      : -1;

    if (currentRank > existingRank) {
      const base = {
        id: collab.entityId,
        name: name || description || "Unknown",
        description,
        permission: collab.policy,
        email: entity.email,
      };

      if (entity.__typename === "PublicUserInvite") {
        collaboratorMap.set(collab.entityId, {
          ...base,
          entityType: "publicUserInvite",
          status: "inviteSent",
          role: entity.userGroup.name,
          userGroupId: entity.userGroup.id,
        });
      } else {
        collaboratorMap.set(collab.entityId, {
          ...base,
          entityType: "publicUser",
          status: entity.status,
          role: entity.userGroups?.[0]?.name,
          userGroupId: entity.userGroups?.[0]?.id,
        });
      }
    }
  });

  return Array.from(collaboratorMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
};

export const mapGroupPermissionsFromStep = (
  collaborators: StepCollaborator[],
): InheritedPermissionGroup[] => {
  if (!collaborators) return [];

  const groups: Array<
    Extract<StepCollaborator, { __typename?: "WorkflowStepCollaboratorGroup" }>
  > = [];

  for (const collab of collaborators) {
    if (collab.__typename === "WorkflowStepCollaboratorGroup") {
      groups.push(collab);
    }
  }

  return groups
    .filter(group => arrayHasAtLeastOneItem(group.members))
    .map(group => ({
      id: group.id,
      label: `${group.name}s in the workflow`,
      collaborators: group.members.map(member => ({
        id: member.id,
        name: `${member.firstName} ${member.lastName}`.trim(),
        description: member.email,
        permission: group.policy,
      })),
    }));
};

interface WorkflowStepShareFormProps {
  collaborators: Collaborator[];
  inheritedPermissions: InheritedPermissionGroup[];
  onStateChange: (collaborators: Collaborator[]) => void;
  onInheritedPermissionGroupClick: (group: InheritedPermissionGroup) => void;
  isLoading?: boolean;
}

export const WorkflowStepShareForm = ({
  collaborators,
  inheritedPermissions,
  onStateChange,
  onInheritedPermissionGroupClick,
  isLoading,
}: WorkflowStepShareFormProps) => {
  if (isLoading) {
    return <Loading size="medium" />;
  }

  return (
    <ShareForm
      searchPlaceholder="Add email address"
      sectionLabel="Who has access"
      infoText={{
        text: `All Forerunner team members can view this step.`,
        linkText: "Add a team member",
        linkHref: "/settings/users/team",
      }}
      permissionOptions={WORKFLOW_STEP_PERMISSION_OPTIONS}
      initialCollaborators={collaborators}
      onStateChange={onStateChange}
      buttonText="Add"
      includeUsers={false}
      includePublicUsers={true}
      inheritedPermissions={inheritedPermissions}
      onInheritedPermissionGroupClick={onInheritedPermissionGroupClick}
    />
  );
};

interface InheritedPermissionsDetailProps {
  group: InheritedPermissionGroup;
}

export const InheritedPermissionsDetail = ({
  group,
}: InheritedPermissionsDetailProps) => {
  return (
    <FlexColumn gap="m">
      <Body type="regular" size="default" color="contentSecondary">
        The following people have access to this step because they are members
        of the workflow.
      </Body>
      <CollaboratorListContainer>
        {group.collaborators.map(collaborator => (
          <ReadOnlyCollaboratorRow
            key={collaborator.id}
            collaborator={collaborator}
            permissionOptions={WORKFLOW_STEP_PERMISSION_OPTIONS}
          />
        ))}
      </CollaboratorListContainer>
    </FlexColumn>
  );
};
